import type { CreateUserResponse } from "@pokifolio/contracts";
import { Copy } from "lucide-react";
import { useState } from "react";

import { Alert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";

/** Shows a new account's temporary password exactly once. */
export function TemporaryPasswordNotice({
  created,
  onDone,
}: {
  created: CreateUserResponse;
  onDone: () => void;
}) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  async function copy() {
    try {
      await navigator.clipboard.writeText(created.temporaryPassword);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  return (
    <Alert tone="success">
      <p className="font-medium">Account created for {created.user.name}.</p>
      <p className="mt-1">
        Give them this temporary password. It is shown only once and they must change it when they first sign
        in.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <code
          className="rounded bg-card px-3 py-1.5 font-mono text-base text-foreground"
          aria-label="Temporary password"
        >
          {created.temporaryPassword}
        </code>
        <Button variant="secondary" size="sm" onClick={() => void copy()}>
          <Copy className="size-3.5" aria-hidden="true" />
          {copyState === "copied" ? "Copied" : "Copy"}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
      {copyState === "failed" && (
        <p className="mt-2 text-xs">Copying failed. Select the password and copy it manually.</p>
      )}
    </Alert>
  );
}
